import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ChevronLeft, Truck, MapPin, Phone, CreditCard, Package, Loader2,
} from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { formatNPR } from '@/utils/format';
import { vendorApi } from '@/services/api';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Input, useConfirm } from '@nepal-ecom/ui';

type OrderStatus =
  | 'pending' | 'payment_confirmed' | 'processing' | 'packed'
  | 'shipped' | 'out_for_delivery' | 'delivered' | 'cancelled'
  | 'return_requested' | 'refunded';

interface OrderItem {
  id: string;
  productId: string;
  productName: string;
  productImage?: string | null;
  quantity: number;
  unitPrice: string | number;
  totalPrice: string | number;
}

interface Order {
  id: string;
  orderNumber: string;
  status: OrderStatus;
  paymentMethod: string;
  paymentStatus?: string;
  subtotal: string | number;
  shippingFee: string | number;
  total: string | number;
  items: OrderItem[];
  deliveryAddress: {
    fullName?: string;
    phone?: string;
    streetAddress?: string;
    municipality?: string;
    wardNumber?: string;
    district?: string;
    provinceName?: string;
    landmark?: string;
  };
  trackingNumber?: string | null;
  courierName?: string | null;
  createdAt: string;
}

const FLOW: OrderStatus[] = ['pending', 'payment_confirmed', 'processing', 'packed', 'shipped', 'out_for_delivery', 'delivered'];

const STATUS_META: Record<OrderStatus, { label: string; variant: 'success' | 'info' | 'warning' | 'destructive' | 'secondary' }> = {
  pending:           { label: 'Pending',          variant: 'warning' },
  payment_confirmed: { label: 'Payment confirmed',variant: 'info' },
  processing:        { label: 'Processing',       variant: 'info' },
  packed:            { label: 'Packed',           variant: 'info' },
  shipped:           { label: 'Shipped',          variant: 'info' },
  out_for_delivery:  { label: 'Out for delivery', variant: 'info' },
  delivered:         { label: 'Delivered',        variant: 'success' },
  cancelled:         { label: 'Cancelled',        variant: 'destructive' },
  return_requested:  { label: 'Return requested', variant: 'warning' },
  refunded:          { label: 'Refunded',         variant: 'secondary' },
};

const num = (v: string | number | null | undefined) => (v == null ? 0 : typeof v === 'string' ? Number(v) : v);

function nextStatus(s: OrderStatus): OrderStatus | null {
  const i = FLOW.indexOf(s);
  if (i < 0 || i === FLOW.length - 1) return null;
  return FLOW[i + 1];
}

export function OrderDetail() {
  const confirm = useConfirm();
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [showTracking, setShowTracking] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [courierName, setCourierName] = useState('');

  const load = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await vendorApi.getOrder(id);
      setOrder((res.data?.data as Order) ?? null);
    } catch (e) {
      toast.error('Could not load order', { description: (e as Error).message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, [id]);

  const advance = async () => {
    if (!order) return;
    const next = nextStatus(order.status);
    if (!next) return;
    setActing(true);
    try {
      await vendorApi.updateOrderStatus(order.id, next);
      toast.success(`Marked as ${STATUS_META[next].label.toLowerCase()}`);
      await load();
    } catch (e) {
      toast.error('Update failed', { description: (e as Error).message });
    } finally {
      setActing(false);
    }
  };

  const cancel = async () => {
    if (!order) return;
    if (!(await confirm({
      title: 'Cancel this order?',
      description: 'The customer will be notified. This cannot be undone.',
      confirmLabel: 'Cancel order',
      cancelLabel: 'Keep order',
      tone: 'danger',
    }))) return;
    setActing(true);
    try {
      await vendorApi.updateOrderStatus(order.id, 'cancelled', 'Cancelled by vendor');
      toast.success('Order cancelled');
      await load();
    } catch (e) {
      toast.error('Cancel failed', { description: (e as Error).message });
    } finally {
      setActing(false);
    }
  };

  const submitTracking = async () => {
    if (!order || !trackingNumber.trim() || !courierName.trim()) return;
    setActing(true);
    try {
      await vendorApi.addTracking(order.id, trackingNumber.trim(), courierName.trim());
      toast.success('Tracking saved');
      setShowTracking(false);
      await load();
    } catch (e) {
      toast.error('Save failed', { description: (e as Error).message });
    } finally {
      setActing(false);
    }
  };

  if (loading) {
    return <div className="py-12 text-center"><Loader2 className="h-6 w-6 mx-auto animate-spin text-slate-400" /></div>;
  }

  if (!order) {
    return (
      <div className="animate-fade-in space-y-4">
        <Link to="/orders" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900">
          <ChevronLeft className="h-4 w-4" /> Orders
        </Link>
        <Card>
          <CardContent className="p-10 text-center">
            <p className="font-semibold text-slate-900">Order not found</p>
            <Button onClick={() => navigate('/orders')} className="mt-4">Back to orders</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const meta = STATUS_META[order.status] ?? { label: order.status, variant: 'secondary' as const };
  const next = nextStatus(order.status);
  const isTerminal = ['delivered', 'cancelled', 'refunded'].includes(order.status);

  return (
    <div className="animate-fade-in space-y-4">
      <Link to="/orders" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900">
        <ChevronLeft className="h-4 w-4" /> Orders
      </Link>

      <PageHeader
        title={`Order #${order.orderNumber}`}
        description={`Placed ${new Date(order.createdAt).toLocaleString()}`}
        badge={<Badge variant={meta.variant}>{meta.label}</Badge>}
        actions={
          <div className="flex items-center gap-2">
            {!isTerminal && next && (
              <Button onClick={advance} disabled={acting}>
                Mark as {STATUS_META[next].label.toLowerCase()}
              </Button>
            )}
            {!isTerminal && order.status === 'packed' && (
              <Button variant="outline" onClick={() => setShowTracking((v) => !v)} disabled={acting}>
                <Truck className="h-4 w-4" /> Add tracking
              </Button>
            )}
            {!isTerminal && (
              <Button variant="outline" onClick={cancel} disabled={acting} className="text-red-600 border-red-200 hover:bg-red-50">
                Cancel order
              </Button>
            )}
          </div>
        }
      />

      {showTracking && (
        <Card>
          <CardHeader><CardTitle className="text-base">Add tracking information</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-700">Courier</label>
                <Input value={courierName} onChange={(e) => setCourierName(e.target.value)} placeholder="Aramex / Pathao / Nepal Post" className="mt-1" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700">Tracking number</label>
                <Input value={trackingNumber} onChange={(e) => setTrackingNumber(e.target.value)} className="mt-1" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={submitTracking} disabled={acting}>
                {acting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save tracking'}
              </Button>
              <Button variant="outline" onClick={() => setShowTracking(false)} disabled={acting}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2">
          <CardHeader><CardTitle className="text-base inline-flex items-center gap-2"><Package className="h-4 w-4" /> Items</CardTitle></CardHeader>
          <CardContent>
            <ul className="divide-y divide-slate-100">
              {order.items.map((it) => (
                <li key={it.id} className="flex items-center gap-3 py-3">
                  <div className="h-14 w-14 rounded-lg bg-slate-100 overflow-hidden flex-shrink-0">
                    {it.productImage
                      ? <img src={it.productImage} alt="" className="w-full h-full object-cover" />
                      : <Package className="h-5 w-5 m-4 text-slate-400" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-900 truncate">{it.productName}</p>
                    <p className="text-xs text-slate-500">Qty {it.quantity} × {formatNPR(num(it.unitPrice))}</p>
                  </div>
                  <p className="text-sm font-bold text-slate-900 tabular-nums">{formatNPR(num(it.totalPrice))}</p>
                </li>
              ))}
            </ul>

            <div className="mt-4 pt-4 border-t border-slate-100 space-y-1.5 text-sm">
              <div className="flex justify-between text-slate-600">
                <span>Subtotal</span>
                <span className="tabular-nums">{formatNPR(num(order.subtotal))}</span>
              </div>
              <div className="flex justify-between text-slate-600">
                <span>Shipping</span>
                <span className="tabular-nums">{num(order.shippingFee) > 0 ? formatNPR(num(order.shippingFee)) : 'Free'}</span>
              </div>
              <div className="flex justify-between font-bold text-slate-900 pt-2 border-t border-slate-100">
                <span>Total</span>
                <span className="tabular-nums">{formatNPR(num(order.total))}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card>
            <CardHeader><CardTitle className="text-base inline-flex items-center gap-2"><MapPin className="h-4 w-4" /> Customer & shipping</CardTitle></CardHeader>
            <CardContent className="text-sm space-y-1.5">
              <p className="font-semibold text-slate-900">{order.deliveryAddress.fullName}</p>
              {order.deliveryAddress.phone && (
                <p className="text-xs text-slate-500 inline-flex items-center gap-1.5"><Phone className="h-3 w-3" /> {order.deliveryAddress.phone}</p>
              )}
              <p className="text-slate-700 mt-2 leading-relaxed">
                {order.deliveryAddress.streetAddress}<br />
                {order.deliveryAddress.municipality}{order.deliveryAddress.wardNumber ? `, Ward ${order.deliveryAddress.wardNumber}` : ''}<br />
                {[order.deliveryAddress.district, order.deliveryAddress.provinceName].filter(Boolean).join(', ')}, Nepal
              </p>
              {order.deliveryAddress.landmark && <p className="text-xs text-slate-500 italic mt-1">Landmark: {order.deliveryAddress.landmark}</p>}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base inline-flex items-center gap-2"><CreditCard className="h-4 w-4" /> Payment</CardTitle></CardHeader>
            <CardContent className="text-sm">
              <p className="font-semibold text-slate-900 capitalize">{order.paymentMethod.replace(/_/g, ' ')}</p>
              {order.paymentStatus && <p className="text-xs text-slate-500 capitalize mt-0.5">{order.paymentStatus.replace(/_/g, ' ')}</p>}
            </CardContent>
          </Card>

          {(order.trackingNumber || order.courierName) && (
            <Card>
              <CardHeader><CardTitle className="text-base inline-flex items-center gap-2"><Truck className="h-4 w-4" /> Tracking</CardTitle></CardHeader>
              <CardContent className="text-sm">
                <p className="font-semibold text-slate-900">{order.courierName ?? 'Courier'}</p>
                <p className="text-xs text-slate-500 font-mono mt-0.5">{order.trackingNumber}</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
